import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { slugify, logActivity, formatDate } from "@/lib/cms";
import { useToast } from "@/context/ToastContext";
import type { Project } from "@/lib/types";
import {
  PageHeader,
  Button,
  Card,
  Spinner,
  EmptyState,
  Badge,
  ConfirmDialog,
} from "@/pages/admin/components/ui";
import ProjectForm from "@/pages/admin/proyectos/ProjectForm";

const estadoBadge: Record<string, "primary" | "secondary" | "amber"> = {
  publicado: "primary",
  borrador: "secondary",
  oculto: "amber",
};

const estadoLabel: Record<string, string> = {
  publicado: "Publicado",
  borrador: "Borrador",
  oculto: "Oculto",
};

export default function Proyectos() {
  const { success, error: toastError } = useToast();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [formProject, setFormProject] = useState<Project | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleting, setDeleting] = useState<Project | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const load = useCallback(async () => {
    const { data, error } = await supabase.from("projects").select("*").order("orden");
    if (!error) setProjects((data ?? []) as Project[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function openNew() {
    setFormProject(null);
    setFormOpen(true);
  }

  function openEdit(p: Project) {
    setFormProject(p);
    setFormOpen(true);
  }

  async function move(p: Project, dir: -1 | 1) {
    const sorted = [...projects].sort((a, b) => a.orden - b.orden);
    const idx = sorted.findIndex((x) => x.id === p.id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[target];
    await supabase.from("projects").update({ orden: b.orden }).eq("id", a.id);
    await supabase.from("projects").update({ orden: a.orden }).eq("id", b.id);
    load();
  }

  async function duplicate(p: Project) {
    const { data, error } = await supabase
      .from("projects")
      .insert({
        slug: slugify(`${p.nombre}-copia`) || `${p.slug}-copia`,
        nombre: `${p.nombre} (copia)`,
        cliente: p.cliente,
        categoria: p.categoria,
        categorias: p.categorias,
        descripcion: p.descripcion,
        descripcion_completa: p.descripcion_completa,
        problema: p.problema,
        solucion: p.solucion,
        caracteristicas: p.caracteristicas,
        resultado: p.resultado,
        anio: p.anio,
        url: p.url,
        url_demo: p.url_demo,
        tecnologias: p.tecnologias,
        imagen: p.imagen,
        logo: p.logo,
        estado: "borrador",
        destacado: false,
        orden: p.orden + 1,
        seo_title: p.seo_title,
        seo_description: p.seo_description,
        og_image: p.og_image,
      })
      .select()
      .single();
    if (error) {
      toastError("No se pudo duplicar el proyecto.");
      return;
    }
    await logActivity("duplicó el proyecto", p.nombre);
    success("Proyecto duplicado correctamente.");
    load();
  }

  async function confirmDelete() {
    if (!deleting) return;
    setDeleteLoading(true);
    const { error } = await supabase.from("projects").delete().eq("id", deleting.id);
    setDeleteLoading(false);
    if (error) {
      toastError("No se pudo eliminar el proyecto.");
      return;
    }
    await logActivity("eliminó el proyecto", deleting.nombre);
    success("Proyecto eliminado.");
    setDeleting(null);
    load();
  }

  async function togglePublicado(p: Project) {
    const next = p.estado === "publicado" ? "borrador" : "publicado";
    await supabase.from("projects").update({ estado: next }).eq("id", p.id);
    load();
  }

  function preview(p: Project) {
    const slug = p.slug || p.id;
    window.open(`${__BASE_PATH__ || ""}/proyecto/${slug}`, "_blank");
  }

  const sorted = [...projects].sort((a, b) => a.orden - b.orden);

  return (
    <div>
      <PageHeader
        title="Proyectos"
        subtitle={`${projects.length} proyecto${projects.length === 1 ? "" : "s"} en total`}
        actions={
          <Button onClick={openNew}>
            <i className="ri-add-line" />
            Nuevo proyecto
          </Button>
        }
      />

      {loading ? (
        <Spinner label="Cargando proyectos…" />
      ) : projects.length === 0 ? (
        <Card>
          <EmptyState
            icon="ri-folder-3-line"
            title="Aún no hay proyectos"
            description="Crea tu primer proyecto para que aparezca en el portafolio público."
            action={
              <Button onClick={openNew}>
                <i className="ri-add-line" />
                Nuevo proyecto
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {sorted.map((p, i) => (
            <Card key={p.id} className="p-4 flex items-center gap-4">
              <div className="w-20 h-14 rounded-md overflow-hidden bg-background-100 shrink-0">
                {p.imagen ? (
                  <img src={p.imagen} alt={p.nombre} className="w-full h-full object-cover object-top" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-foreground-300">
                    <i className="ri-image-line" />
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-foreground-900 truncate">{p.nombre}</p>
                  {p.destacado && <i className="ri-star-fill text-amber-400" title="Destacado" />}
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge tone={estadoBadge[p.estado] || "secondary"}>
                    {estadoLabel[p.estado] || p.estado}
                  </Badge>
                  {p.categoria && <span className="text-xs text-foreground-500">{p.categoria}</span>}
                  <span className="text-xs text-foreground-400">{p.anio}</span>
                </div>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => move(p, -1)}
                  disabled={i === 0}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 disabled:opacity-30"
                  title="Subir"
                >
                  <i className="ri-arrow-up-line" />
                </button>
                <button
                  onClick={() => move(p, 1)}
                  disabled={i === sorted.length - 1}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 disabled:opacity-30"
                  title="Bajar"
                >
                  <i className="ri-arrow-down-line" />
                </button>
                <button
                  onClick={() => togglePublicado(p)}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100"
                  title={p.estado === "publicado" ? "Despublicar" : "Publicar"}
                >
                  <i className={p.estado === "publicado" ? "ri-eye-off-line" : "ri-eye-line"} />
                </button>
                <button
                  onClick={() => preview(p)}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100"
                  title="Vista previa"
                >
                  <i className="ri-external-link-line" />
                </button>
                <button
                  onClick={() => duplicate(p)}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100"
                  title="Duplicar"
                >
                  <i className="ri-file-copy-line" />
                </button>
                <button
                  onClick={() => openEdit(p)}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-primary-600 hover:bg-primary-50"
                  title="Editar"
                >
                  <i className="ri-edit-line" />
                </button>
                <button
                  onClick={() => setDeleting(p)}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50"
                  title="Eliminar"
                >
                  <i className="ri-delete-bin-line" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {formOpen && (
        <ProjectForm project={formProject} onClose={() => setFormOpen(false)} onSaved={load} />
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Eliminar proyecto"
        message={`¿Seguro que quieres eliminar "${deleting?.nombre}"? Esta acción no se puede deshacer.`}
        loading={deleteLoading}
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}